import { BusinessMerchantServiceChargeFee } from "../../../interfaces/businessFull.interface";
import {
  OnlineBucketField,
  OnlineBucketGroupCoverageEntry,
  OnlineGroupKey,
} from "../../../interfaces/onlinePricing.interface";
import { ValueType } from "../../../interfaces/partnerDeal.interface";
import { OnlinePricingGroup } from "./sixteenBucketOnlinePricing";

// ---------------------------------------------------------------------------
// Per-bucket online rates.
//
// A 16-bucket merchant is priced on groups, but the rate is stored PER BUCKET,
// and a bucket can be priced away from its group — dev merchant TRES holding
// B.V. carries onlineEeaDebit at 1.49 % among 1.39 % siblings. So a bucket rate
// can never be read off its group; it has to be resolved.
//
// This mirrors the backend's own shared/services/business-pricing-service/
// onlineBucketRates.ts, precedence included. The two must agree: a rate KBM
// renders under a different rule from the one the Adyen split builder prices
// from would show the operator a fee the merchant is not charged.
// ---------------------------------------------------------------------------

export type OnlineBucketRate = { value: number; valueType: ValueType };

/** Rate by bucket field identifier. Carries no grouping of its own — that is
 *  the whole point: two buckets one group covers may differ here. */
export type OnlineBucketRates = Partial<Record<string, OnlineBucketRate>>;

/** A rate as a form holds it, where `null` means the operator has left it empty:
 *  unpriced, which is not the same as free. The backend reads a 0 as a real rate
 *  and persists it silently, so the two must not be conflated anywhere. */
export type OnlineRateDraft = { value: number | null; valueType: ValueType };

const ONLINE_BUCKET_FIELDS: ReadonlySet<string> = new Set<string>(
  Object.values(OnlineBucketField)
);

export const isOnlineBucketField = (name: string): boolean =>
  ONLINE_BUCKET_FIELDS.has(name);

/** The unit a bucket starts in when nothing is stored for it, undefined where
 *  there is no obvious one. Every bucket may be priced in either unit, so this
 *  is a default and never a restriction on what the operator may pick. */
export function onlineBucketValueType(field: string): ValueType | undefined {
  if (field === OnlineBucketField.IDeal) return "Fixed";
  if (field === OnlineBucketField.Bancontact) return undefined;
  return "Percentage";
}

/** Every bucket the merchant's groups carry, in display order. */
export const carriedBucketFields = (groups: OnlinePricingGroup[]): string[] =>
  groups.flatMap((group) => group.buckets.map((bucket) => bucket.field));

/** Stored rows as rates. A row naming something that is not an online bucket
 *  prices no bucket — a four-bucket merchant's display-name rows land here — so
 *  it is skipped rather than trusted. */
export function onlineBucketRatesFromFees(
  fees: BusinessMerchantServiceChargeFee[] | undefined
): OnlineBucketRates {
  const rates: OnlineBucketRates = {};
  (fees ?? []).forEach((fee) => {
    if (!isOnlineBucketField(fee.name)) return;
    if (typeof fee.value !== "number") return;
    rates[fee.name] = { value: fee.value, valueType: fee.valueType };
  });
  return rates;
}

/** Whether rows price every bucket the groups carry. A partial set is
 *  indistinguishable from a stale one, so it is worth nothing: the backend
 *  defers to the group fan-out wholesale rather than mixing the two, and mixing
 *  them here would render rates it will not price. */
export function coversCarriedBuckets(
  rates: OnlineBucketRates,
  groups: OnlinePricingGroup[]
): boolean {
  const fields = carriedBucketFields(groups);
  return fields.length > 0 && fields.every((field) => rates[field]);
}

/** A group's rate fanned out over every bucket it covers: what a merchant on a
 *  partner inherits, and the fallback for a merchant whose document predates
 *  per-bucket rates. A group with no rate yields no rows — an absent rate reads
 *  as unpriced, where a 0 would read as free. */
export function onlineBucketRatesFromGroups(
  groups: OnlinePricingGroup[],
  groupRates: Partial<Record<OnlineGroupKey, OnlineRateDraft>>
): OnlineBucketRates {
  const rates: OnlineBucketRates = {};
  groups.forEach((group) => {
    const rate = groupRates[group.key];
    if (typeof rate?.value !== "number") return;
    group.buckets.forEach((bucket) => {
      rates[bucket.field] = {
        value: rate.value as number,
        valueType: rate.valueType,
      };
    });
  });
  return rates;
}

/** The rates the preview resolved, or undefined when it sent none. `buckets` is
 *  the group → bucket mapping and says nothing about rates, so a payload
 *  without `fees` has to be answered elsewhere, not read as "all zero". */
export function onlineBucketRatesFromCoverage(
  coverage: OnlineBucketGroupCoverageEntry[] | undefined
): OnlineBucketRates | undefined {
  if (!coverage?.some((entry) => entry.fees?.length)) return undefined;
  const rates: OnlineBucketRates = {};
  coverage.forEach((entry) => {
    (entry.fees ?? []).forEach((fee) => {
      if (typeof fee.value !== "number" || fee.value <= 0) return;
      rates[fee.name] = { value: fee.value, valueType: fee.valueType };
    });
  });
  if (!Object.keys(rates).length) return undefined;
  return rates;
}

/** The rate every bucket carries as persisted — the merchant's truth before any
 *  edit in the form.
 *
 *  The backend's precedence, in the order KBM can know it:
 *
 *  1. the preview's own `fees`, already resolved server-side;
 *  2. else the merchant's stored rows;
 *  3. else the group rate fanned out, which is the pre-per-bucket behaviour.
 *
 *  A set that prices every carried bucket is returned as it stands. A partial
 *  set is no longer thrown away wholesale: coverage naming a bucket the merchant
 *  does not carry — a leftover wallet key — or a preview that prices only some
 *  of them used to re-seed the whole form from the group fan-out, losing the
 *  per-bucket rates the operator had just saved. So the three are layered
 *  instead, and the fan-out only fills what neither of the other two priced.
 *
 *  Step 2 is not redundant with step 1: the preview endpoint requires a
 *  partnerId, so a merchant with no partner never gets one — and that is
 *  exactly the merchant allowed to diverge from its groups. Reading its stored
 *  rows directly resolves the same rate the backend would. */
export function resolveOnlineBucketRates(params: {
  groups: OnlinePricingGroup[];
  coverage?: OnlineBucketGroupCoverageEntry[];
  storedFees?: BusinessMerchantServiceChargeFee[];
  groupRates: Partial<Record<OnlineGroupKey, OnlineRateDraft>>;
}): OnlineBucketRates {
  const previewed = onlineBucketRatesFromCoverage(params.coverage);
  if (previewed && coversCarriedBuckets(previewed, params.groups)) {
    return previewed;
  }
  const stored = onlineBucketRatesFromFees(params.storedFees);
  if (coversCarriedBuckets(stored, params.groups)) return stored;
  const fromGroups = onlineBucketRatesFromGroups(
    params.groups,
    params.groupRates
  );
  return { ...fromGroups, ...stored, ...previewed };
}

const applyRateDraft = (
  rates: OnlineBucketRates,
  field: string,
  draft: OnlineRateDraft
) => {
  // An emptied input leaves the bucket unpriced, so the row goes rather than
  // keeping the rate the operator just cleared. The save is blocked on it —
  // see bucketsMissingARate.
  if (typeof draft.value !== "number") delete rates[field];
  else rates[field] = { value: draft.value, valueType: draft.valueType };
};

/** The rates the form holds: the persisted truth, then the buckets the operator
 *  edited. A group carries no rate of its own to layer in between — the buckets
 *  under it are each edited directly. */
export function formOnlineBucketRates(params: {
  persisted: OnlineBucketRates;
  editedBuckets: Partial<Record<string, OnlineRateDraft>>;
}): OnlineBucketRates {
  const rates: OnlineBucketRates = { ...params.persisted };
  Object.entries(params.editedBuckets).forEach(([field, draft]) => {
    if (draft) applyRateDraft(rates, field, draft);
  });
  return rates;
}

export type OnlineGroupBucketSpread = {
  // True when the group's buckets do not all carry one rate, which is what
  // makes a single group value the wrong thing to show for them.
  diverged: boolean;
  min: number;
  max: number;
  // Absent when the buckets are not all priced in the same unit, which leaves
  // the spread unrenderable as a range — €0.16 to 1.49 % is not a range.
  valueType?: ValueType;
};

/** The spread of rates across a group's buckets, or undefined when none of them
 *  is priced. */
export function onlineGroupBucketSpread(
  group: OnlinePricingGroup,
  rates: OnlineBucketRates
): OnlineGroupBucketSpread | undefined {
  const priced = group.buckets
    .map((bucket) => rates[bucket.field])
    .filter((rate): rate is OnlineBucketRate => !!rate);
  if (priced.length === 0) return undefined;
  const values = priced.map((rate) => rate.value);
  const valueTypes = new Set(priced.map((rate) => rate.valueType));
  const min = Math.min(...values);
  const max = Math.max(...values);
  return {
    diverged: valueTypes.size > 1 || min !== max,
    min,
    max,
    valueType: valueTypes.size === 1 ? priced[0].valueType : undefined,
  };
}

/** The group rates a save can honestly carry: one per group whose buckets all
 *  agree on a rate.
 *
 *  A merchant's price lives on its buckets, so a group whose buckets diverge has
 *  no rate that describes them and is left out rather than guessed at. The write
 *  path completes such a group from the rows sent alongside, and only a group the
 *  payload does carry is checked against the partner band. */
export function groupRatesFromBuckets(
  groups: OnlinePricingGroup[],
  rates: OnlineBucketRates
): Partial<Record<OnlineGroupKey, OnlineRateDraft>> {
  const derived: Partial<Record<OnlineGroupKey, OnlineRateDraft>> = {};
  groups.forEach((group) => {
    const spread = onlineGroupBucketSpread(group, rates);
    if (!spread || spread.diverged || !spread.valueType) return;
    derived[group.key] = { value: spread.min, valueType: spread.valueType };
  });
  return derived;
}

/** The `onlineMerchantServiceChargeFees` a save carries: one row per bucket the
 *  merchant's groups cover, in group order. The backend requires the set to be
 *  complete and rejects a duplicate or a bucket the template does not price, so
 *  the group set — not the stored rows — is what it is built from. */
export function buildOnlineMerchantServiceChargeFees(
  groups: OnlinePricingGroup[],
  rates: OnlineBucketRates
): BusinessMerchantServiceChargeFee[] {
  const fees: BusinessMerchantServiceChargeFee[] = [];
  const fields = carriedBucketFields(groups);
  const source = fields.length
    ? fields
    : Object.keys(rates).filter(isOnlineBucketField);
  source.forEach((field) => {
    const rate = rates[field];
    if (!rate) return;
    fees.push({ name: field, value: rate.value, valueType: rate.valueType });
  });
  return fees;
}

/** Buckets the payload could not carry a rate for. An incomplete set is a 422
 *  INVALID_ONLINE_PRICING that persists nothing, so it has to be stopped in the
 *  form where the operator can still see which bucket is empty. */
export function bucketsMissingARate(
  groups: OnlinePricingGroup[],
  rates: OnlineBucketRates
): { field: string; label: string }[] {
  return groups
    .flatMap((group) => group.buckets)
    .filter((bucket) => !rates[bucket.field]);
}
